import { deserialize, serialize } from 'class-transformer';
import { Subject } from 'rxjs';

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { AuthenticatedUser } from '../model/authentication/authenticated-user.model';
import { PWUtils } from '../shared/pw-utils';
import { ApiConfigService } from './api-config.service';
import { DialogMessage } from './dialog-message.service';
import { JwtHelperService } from './jwthelper.service';
import { LocalStorageService } from './local-storage.service';

@Injectable()
export class AuthenticationService {

  refreshTokenUrl = this.apiConfigService.accountApiUrl + 'RefreshToken';

  private authSuccessSubject = new Subject<string>();
  authSuccess$ = this.authSuccessSubject.asObservable();

  private authenticatedUserObj: AuthenticatedUser;
  private jwtHelper = new JwtHelperService();
  public authenticatedUserStorageKey = 'authenticatedUserKey';

  constructor(
    private localStorageService: LocalStorageService,
    private apiConfigService: ApiConfigService,
    private dialogMessage: DialogMessage,
    private router: Router
  ) { }

  get authenticatedUser(): AuthenticatedUser {
    if (!this.authenticatedUserObj) {
      this.authenticatedUserObj = this.getUserFromLocal;
    }

    return this.authenticatedUserObj;
  }

  authSuccess(token: string, rememberMe: boolean): void {
    const decodedToken = this.jwtHelper.decodeToken(token);
    const authenticatedUser = new AuthenticatedUser();
    authenticatedUser.token = token;
    authenticatedUser.rememberMe = rememberMe;
    const decodedTokenKeys = Object.keys(decodedToken);
    decodedTokenKeys.forEach(decodedTokenKey => {
      const decodedTokenValue = typeof decodedToken[decodedTokenKey] === 'number' ?
        +decodedToken[decodedTokenKey] :
        decodedToken[decodedTokenKey];
      authenticatedUser[PWUtils.toCamcelCase(decodedTokenKey)] = decodedTokenValue;
    });

    this.saveUser(authenticatedUser);
    this.authSuccessSubject.next(token);
  }

  authToken(token: string): AuthenticatedUser {
    if (token) {
      if (this.jwtHelper.isTokenExpired(token)) {
        this.dialogMessage.showInfoMessage('Reset password token has expired!', () => {
          this.router.navigateByUrl('/login');
        });
        return undefined;
      } else {
        this.authSuccess(token, true);
        return this.authenticatedUser;
      }
    } else {
      this.router.navigateByUrl('/login');
      return undefined;
    }
  }

  clearAuthenticatedUser(): void {
    this.authenticatedUserObj = null;
    this.localStorageService.removeItem(this.authenticatedUserStorageKey);
  }

  saveUser(auth: AuthenticatedUser): void {
    this.authenticatedUserObj = auth;
    this.authenticatedUserObj.id = Number(auth.id);
    this.saveUserToLocal(auth);
  }

  private saveUserToLocal(auth: AuthenticatedUser): void {
    this.localStorageService.setItem(
      this.authenticatedUserStorageKey,
      serialize(auth)
    );
  }

  private get getUserFromLocal(): AuthenticatedUser {
    return deserialize(
      AuthenticatedUser,
      this.localStorageService.getItem(this.authenticatedUserStorageKey)
    );
  }
}
